// Birtvy's slide domain — the slide type, its serialized shape, canvas grid geometry, and the pure slide
// ops (shape mapping, baked→html, bulk generation, clone). Mirrors birtvyDoc.ts / birtvyNote.ts /
// birtvyImage.ts (one domain module per item kind). No React state here — BirtvyCanvas owns that.
import { parseArtifactManifest, extractFontHrefs } from '../birtvyOpen/artifactMeta'
import { asLayoutPattern, type LayoutPattern } from '../style/styleRecord'
import { renderSlideElementsToHtml } from '~/design-system/components/DeckGen/canvasEngine/labSlideHtml'
import type { LabShape, LabSlideBake } from '~/design-system/components/DeckGen/canvasEngine/types'
import type { SlideElement, Slide } from '~/design-system/components/DeckGen/types/presentation'
import type { GenerationModel } from '~/design-system/components/DeckGen/ai/deckGenSchema'

// The slide toolbar uses the product's real model picker (full generation-model list), so Birtvy's model is
// the full GenerationModel union — not just the two Gemini ids. Default stays Gemini 3.5 Flash (cheap).
export type BirtvyModel = GenerationModel

/** Slide render size (world px) — the product's 16:9 slide. */
export const SLIDE_W = 1920
export const SLIDE_H = 1080
/** Gap + column count for the default slide grid (addSlide placement). */
export const GAP = 200
export const COLS = 4

export type SlideStatus = 'empty' | 'generating' | 'done'

// ── The ARTIFACT record (docs/ARTIFACTS_PLAN.md §7) ──────────────────────────────────────────────────
// A doc slide's metadata as REAL fields instead of data smuggled inside the html strings. Everything here
// is plain JSON data: it rides getState/onHydrate, the undo history and the item rows untouched, so the
// getState ∘ onHydrate fixed point holds by construction.

/** Who performed a doc-source write. 'bridge' = window.__birtvyOpen; 'retheme' = deterministic token
 *  swap; 'logos' = the verified-marks pass filling data-logo declarations; 'style' = a hand edit in the
 *  artifact editor (the user moved a control, no model involved). */
export type DocWriteBy = 'chat' | 'inbox' | 'bridge' | 'undo' | 'retheme' | 'logos' | 'style' | 'mcp' // 'mcp' = a write from the MCP server

/** Where an artifact came from — replaces the old `prompt: "inbox:<key>"` hack. */
export interface DocProvenance {
  by: DocWriteBy
  /** The inbox file key that owns this slide (file-transport identity, was abused into `prompt`). */
  inboxKey?: string
  /** Drill-down parent: the artifact this one was broken out of. */
  parent?: number
}

/** One line of the activity log: what happened to the source, when, by which prompt. */
export interface DocLogEntry {
  at: number
  by: DocWriteBy
  rev: number
  prompt?: string
  /** Contract-validator taste findings recorded against this write (never blocking). */
  flags?: string[]
  note?: string
  /** The composition family the writer was TOLD to build. `DocMeta.composition` is what landed; this is
   *  the fallback when the model forgot the manifest key, and it survives later writes because the log
   *  only ever appends. */
  composition?: LayoutPattern
  /** The kit role this write was handed (`DocMeta.role` at the time of the write). On the log so a later
   *  write that names a new role leaves a record of which one each revision was built for. */
  role?: string
}

/** Extracted view of the artifact (manifest fields + fonts) — DERIVED from docHtml at write time, never
 *  edited directly. Source wins on any disagreement (the originals-vs-instances rule). */
export interface DocMeta {
  title?: string
  aesthetic?: string
  theme?: 'deck' | 'own'
  /** Deck runtime version the source assumes; the pin that lets a future runtime v2 keep serving v1. */
  runtime?: number
  sequenceMs?: number
  steps?: number
  /** The structure the document declares in its manifest, normalised to the vocabulary. */
  composition?: LayoutPattern
  /**
   * The kit ROLE this document does — `cover`, `metric-proof`, the brand's own `pricing-table`.
   *
   * THE ONE FIELD NOT EXTRACTED FROM SOURCE. Everything else on this record is re-read from the html on
   * every write (source wins); a role is the JOB the writer was handed, which the manifest does not carry
   * (`artifactContract.ts` names its keys exactly, and this is not one of them). So the write funnel
   * carries it forward on every write unless the write names a new one, and `roleOfDoc` prefers it over
   * the family-based guess — which is what lets an EDIT reapply the role's standing rules instead of
   * resolving "single-number" back to whichever role happens to own that family. Written by fresh
   * generations that resolved a role; absent on documents made before it existed.
   */
  role?: string
  fonts?: string[]
  provenance?: DocProvenance
}

export interface BirtvySlide {
  id: number
  x: number
  y: number
  status: SlideStatus
  prompt: string
  html?: string
  baked?: LabSlideBake
  /** A Graphy chart config (from a "build with graphy" note → /birtvy-graphy). When set, the slide is a chart
   *  slide: its `baked` holds the title/caption text (buildGraphyBaked) and the live Graphy chart is drawn as
   *  an overlay at GRAPHY_CHART_RECT. Opaque here — Graphy validates its shape. */
  graphyConfig?: unknown
  /** Slide-group membership (see birtvyGroup.ts): the id of the BirtvySlideGroup this slide belongs to, or
   *  null/undefined when it's a loose slide. A group frame wraps every group with ≥2 members. */
  groupId?: number | null
  /** When true, the slide stays on the canvas but is skipped in Present mode (default/undefined = included).
   *  DeckGen parity: Slide.excludedFromPresentation (types/presentation.ts). Export is NOT filtered. */
  excludedFromPresentation?: boolean
  /** OPEN-SLIDE-CLASS slide (the Birtvy Open experiment folded into the real canvas): a COMPLETE
   *  self-contained HTML document rendered live in a sandboxed iframe. When set it is the slide's whole
   *  content — `baked`/`html` are ignored by the card — and the slide is deliberately NOT element-editable:
   *  the document is the artifact, prompts restyle it (see the doc branch in editSlide/openSlide). */
  docHtml?: string
  /** The settled static HTML baked from docHtml — ALWAYS cleared when docHtml changes; regenerated lazily
   *  by the card's bake queue. */
  docBaked?: string
  /** ARTIFACT record fields (docs/ARTIFACTS_PLAN.md §7). All maintained by writeDocSource in BirtvyCanvas —
   *  the ONE mutation path every source write goes through, so rev/log/prev can never drift apart. */
  docMeta?: DocMeta
  /** Monotonic source revision. Absent = pre-record artifact (treated as 0); first write makes it 1. */
  docRev?: number
  /** Newest-last activity log, capped at DOC_LOG_CAP. */
  docLog?: DocLogEntry[]
  /** The previous source, held for exactly one step of undo (sources run 10-30KB; deep history is a
   *  backend concern, not a blob concern). */
  docPrevSource?: string
}
export interface SerializedSlide {
  id: number
  x: number
  y: number
  prompt: string
  html?: string
  baked?: LabSlideBake
  graphyConfig?: unknown
  groupId?: number | null
  /** Persisted so exclusion survives reload, the public share link AND undo/redo — the history snapshot is
   *  JSON.stringify(getState()), so a field missing here is wiped by every ⌘Z. */
  excludedFromPresentation?: boolean
  /** Same persistence rule as above: absent here = wiped by every undo. */
  docHtml?: string
  /** The settled static HTML baked from docHtml — cleared with every docHtml write, so it's only ever
   *  persisted in sync with the document it was baked from. */
  docBaked?: string
  /** The artifact record — persisted whole so history, provenance and rev survive reload AND ⌘Z (the
   *  history snapshot is JSON.stringify(getState()); a field missing here is wiped by every undo). */
  docMeta?: DocMeta
  docRev?: number
  docLog?: DocLogEntry[]
  docPrevSource?: string
}

/** Activity-log length cap — one artifact's log stays a few KB even after heavy editing. */
export const DOC_LOG_CAP = 50

/**
 * Derive the artifact record's meta from its SOURCE (manifest fields + font links) — extraction, never
 * authoring: if the new source lacks a manifest, no stale title lingers, because the source always wins
 * (the originals-vs-instances rule). Provenance is the one field the caller owns; omitted = unknown origin
 * (pre-record artifacts), which stays honestly absent rather than guessed.
 */
export function docMetaFromSource(html: string, provenance?: DocProvenance): DocMeta {
  const m = parseArtifactManifest(html)
  const fonts = extractFontHrefs(html)
  const composition = asLayoutPattern(m?.composition)
  return {
    ...(typeof m?.title === 'string' && m.title ? { title: m.title } : {}),
    ...(composition ? { composition } : {}),
    ...(typeof m?.aesthetic === 'string' && m.aesthetic ? { aesthetic: m.aesthetic } : {}),
    ...(m?.theme === 'deck' || m?.theme === 'own' ? { theme: m.theme } : {}),
    ...(typeof m?.runtime === 'number' && Number.isFinite(m.runtime) ? { runtime: m.runtime } : {}),
    ...(typeof m?.sequenceMs === 'number' && Number.isFinite(m.sequenceMs) ? { sequenceMs: m.sequenceMs } : {}),
    ...(typeof m?.steps === 'number' && Number.isFinite(m.steps) ? { steps: m.steps } : {}),
    ...(fonts.length ? { fonts } : {}),
    ...(provenance ? { provenance } : {}),
  }
}

export function toShape(s: BirtvySlide): LabShape {
  return { id: s.id, kind: 'slide', x: s.x, y: s.y, w: SLIDE_W, h: SLIDE_H, html: s.html, generating: s.status === 'generating', baked: s.baked }
}

/** The chart's INITIAL box (world px) — the RIGHT column. After generation the chart lives as a real
 *  placeholder element (buildGraphyBaked), so it can be dragged/resized; this is just where it starts. */
export const GRAPHY_CHART_RECT = { x: 872, y: 170, width: 916, height: 740 }

/** True for the invisible placeholder element that stands in for the Graphy chart. It's a normal (transparent)
 *  shape so it selects / drags / resizes / shows in Layers like any element; the live chart is drawn as an
 *  overlay tracking its geometry (see BirtvySlideCard / BirtvySlideEditor). */
export function isGraphyChartEl(el: { id: string }): boolean {
  return el.id.endsWith('gchart')
}

/** The chart's box for a graphy slide — the chart element's live geometry, so the card's chart overlay
 *  stays in sync with a moved/resized chart. Returns null when the chart element was deleted (the card then
 *  draws no chart — no orphan). The editor renders the chart INSIDE the element, so it doesn't use this. */
export function graphyChartGeom(baked?: LabSlideBake): { x: number; y: number; width: number; height: number } | null {
  const el = baked?.elements.find(isGraphyChartEl)
  return el ? { x: el.x, y: el.y, width: el.width, height: el.height } : null
}

/**
 * Build the baked slide for a "build with graphy" chart: the storytelling title + caption as REAL, editable
 * text elements in the left column. The chart itself isn't a baked element (it's a live React component drawn
 * as an overlay at GRAPHY_CHART_RECT) — so the slide opens in the normal slide editor, where the text edits
 * like any other slide, and the chart sits alongside it.
 */
export function buildGraphyBaked(id: number, title: string, caption: string): LabSlideBake {
  const LEFT_X = 130
  const LEFT_W = 680
  const elements: SlideElement[] = [
    {
      id: `birtvy${id}gtitle`,
      type: 'text',
      x: LEFT_X,
      y: 356,
      width: LEFT_W,
      height: 240,
      zIndex: 2,
      text: title || 'Untitled chart',
      textRole: 'H2',
      fontSize: 72,
      lineHeight: 1.08,
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontWeight: 700,
      fontStyle: 'normal',
      letterSpacing: -1,
      textAlign: 'left',
      listStyle: 'none',
      textSizeMode: 'auto-height',
      color: '#18181b',
      background: 'transparent',
    },
  ]
  if (caption && caption.trim()) {
    elements.push({
      id: `birtvy${id}gcap`,
      type: 'text',
      x: LEFT_X,
      y: 648,
      width: LEFT_W,
      height: 120,
      zIndex: 2,
      text: caption,
      textRole: 'Body',
      fontSize: 30,
      lineHeight: 1.45,
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontWeight: 400,
      fontStyle: 'normal',
      textAlign: 'left',
      listStyle: 'none',
      textSizeMode: 'auto-height',
      color: '#52525b',
      background: 'transparent',
    })
  }
  // The chart's placeholder: a transparent shape at the initial rect. It's a real element (selectable,
  // draggable, resizable, in Layers); the live chart is painted as an overlay tracking its geometry.
  elements.push({
    id: `birtvy${id}gchart`,
    type: 'shape',
    shapeKind: 'rectangle',
    x: GRAPHY_CHART_RECT.x,
    y: GRAPHY_CHART_RECT.y,
    width: GRAPHY_CHART_RECT.width,
    height: GRAPHY_CHART_RECT.height,
    zIndex: 1,
    background: 'transparent',
    // Explicit 0 stroke — WITHOUT this the shape renderer defaults to a 2px #93c5fd (light-blue) border.
    strokeWidth: 0,
    name: 'Chart',
  })
  return { background: '#ffffff', elements }
}

/** A transparent chart placeholder element at a given rect (id ends `gchart`, so isGraphyChartEl matches and
 *  the live Graphy chart draws over it). Used when a GENERATED slide's data chart is routed to Graphy: the
 *  slide keeps its own text elements; only the chart's slot becomes this placeholder + a slide graphyConfig. */
export function chartPlaceholderElement(slideId: number, rect: { x: number; y: number; width: number; height: number }, zIndex: number): SlideElement {
  return {
    id: `birtvy${slideId}gchart`,
    type: 'shape',
    shapeKind: 'rectangle',
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    zIndex,
    background: 'transparent',
    strokeWidth: 0,
    name: 'Chart',
  }
}

export function bakedToHtml(baked: LabSlideBake, id: number): string {
  try {
    // `updatedAt` busts renderSlideElementsToHtml's `${id}:${updatedAt}` cache — without it every call for
    // the same id returns the FIRST render (undefined key), so edits/snapshots would never reach the html.
    return renderSlideElementsToHtml({ id: String(id), updatedAt: Date.now(), elements: baked.elements, background: baked.background, fillGradient: baked.fillGradient } as unknown as Slide)
  } catch {
    return ''
  }
}

/** The chart element's captured PNG (set once the on-canvas chart is snapshotted), or undefined while the
 *  chart still renders live on the card. Once set, the card renders pure baked HTML — the 120fps path. */
export function graphyChartImage(baked?: LabSlideBake): string | undefined {
  return baked?.elements.find(isGraphyChartEl)?.fillImage
}

/** Clone a baked slide with fresh element ids so each cloned slide edits independently. Group membership
 *  (parentId / childIds) is remapped through the same id map, so grouped elements stay grouped in the clone
 *  instead of pointing at the original's ids (or dangling). */
export function cloneBaked(baked: LabSlideBake, newId: number): LabSlideBake {
  // The chart placeholder's id SUFFIX is semantic: isGraphyChartEl matches `…gchart`, and that match is how
  // the card mounts the live chart overlay / applies its snapshot. A plain positional rename silently
  // orphaned the clone's chart — the copy had graphyConfig + placeholder but nothing ever drew or baked it.
  const idMap = new Map(baked.elements.map((el, i) => [el.id, isGraphyChartEl(el) ? `birtvy${newId}gchart` : `birtvy${newId}e${i}`]))
  return {
    background: baked.background,
    fillGradient: baked.fillGradient,
    elements: baked.elements.map((el) => ({
      ...el,
      id: idMap.get(el.id)!,
      parentId: el.parentId != null ? idMap.get(el.parentId) ?? null : el.parentId,
      childIds: el.childIds?.map((c) => idMap.get(c) ?? c),
    })),
  }
}
